import React from 'react';
import { Box } from '@mui/material';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { InterestInputField, MultiSelectWithCustomOptions, SwitchField } from 'components/form';
import { termsOptions } from 'modules/desking/constants';
import { FinanceProgramForm } from 'modules/financeProgram/schema';

interface Props {
  isReadOnly: boolean;
}

export const SubsidySection = ({ isReadOnly }: Props) => {
  const { control, setValue } = useFormContext<FinanceProgramForm>();

  const secondStep = useWatch({ control, name: 'program.second' });

  const { fields: specifySubsidyRatesFields } = useFieldArray({
    control,
    name: 'program.second.specifySubsidyRates',
  });

  const handleChangeSubsidyTerms = (selected: (string | number)[]) => {
    const termsToString = selected.map(String);

    setValue('program.second.subsidyTerms', termsToString, { shouldValidate: true });
    setValue(
      'program.second.specifySubsidyRates',
      termsToString.map(term => ({
        term,
        subsidyPercent: secondStep.subsidyPercent ?? 0,
        dnetPercent: secondStep.dnetPercent ?? 0,
      }))
    );
  };

  return (
    <>
      <Box className="formRow">
        <MultiSelectWithCustomOptions
          isOptionsSorted
          control={control}
          options={termsOptions}
          name="program.second.subsidyTerms"
          onChange={handleChangeSubsidyTerms}
          selectProps={{ disabled: isReadOnly, label: 'Terms in Months' }}
          selectFormControlProps={{ required: true, variant: 'standard' }}
          inputProps={{ type: 'number', label: 'Add custom term', placeholder: 'Enter your own term' }}
        />
      </Box>

      <Box className="formRow">
        <InterestInputField
          control={control}
          label="Subsidy %"
          name="program.second.subsidyPercent"
          textFieldProps={{ fullWidth: true, disabled: isReadOnly || secondStep.isSpecifySubsidy }}
        />

        <InterestInputField
          control={control}
          label="DNET %"
          name="program.second.dnetPercent"
          textFieldProps={{ fullWidth: true, disabled: isReadOnly || secondStep.isSpecifySubsidy }}
        />
      </Box>

      <Box>
        <SwitchField
          control={control}
          disabled={isReadOnly}
          label="Specify subsidy per term"
          name="program.second.isSpecifySubsidy"
        />
      </Box>

      {!!secondStep.isSpecifySubsidy && !!specifySubsidyRatesFields.length && (
        <>
          {specifySubsidyRatesFields.map((field, index) => (
            <Box key={field.id} className="formRow">
              <InterestInputField
                control={control}
                label={`Subsidy % — ${secondStep.subsidyTerms?.[index] ?? ''} months`}
                name={`program.second.specifySubsidyRates.${index}.subsidyPercent`}
                textFieldProps={{ required: true, fullWidth: true, disabled: isReadOnly }}
              />

              <InterestInputField
                control={control}
                label={`DNET % — ${secondStep.subsidyTerms?.[index] ?? ''} months`}
                name={`program.second.specifySubsidyRates.${index}.dnetPercent`}
                textFieldProps={{ required: true, fullWidth: true, disabled: isReadOnly }}
              />
            </Box>
          ))}
        </>
      )}
    </>
  );
};
